/**
 * Attraction Events Redux Slice
 * Manages events for attractions (from ticket vendors) with normalized state and caching
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getEventsByAttraction, getEventById } from '../util/api/supabaseApi';

// Cache TTL: 2 minutes for events
const EVENTS_CACHE_TTL = 2 * 60 * 1000;

// Async thunks
export const fetchEventsByAttraction = createAsyncThunk(
  'attractionEvents/fetchByAttraction',
  async ({ attractionId, page = 0, limit = 20 }, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const key = typeof attractionId === 'string' ? parseInt(attractionId) : attractionId;
      const cached = state.attractionEvents?.byAttraction?.[key];

      // Return cached if fresh and it's page 0 (first page)
      if (
        page === 0 &&
        cached &&
        cached.lastFetched &&
        Date.now() - cached.lastFetched < EVENTS_CACHE_TTL
      ) {
        const events = cached.eventIds
          .map(id => state.attractionEvents?.byId?.[id])
          .filter(Boolean);

        if (events.length > 0) {
          return {
            attractionId,
            events,
            total: cached.total,
            hasMore: cached.hasMore,
            currentPage: cached.currentPage || 0,
          };
        }
      }
      const result = await getEventsByAttraction(attractionId, page, limit);
      return { attractionId, ...result };
    } catch (error) {
      console.error('❌ Redux: Error fetching events:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchEventDetails = createAsyncThunk(
  'attractionEvents/fetchDetails',
  async (eventId, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const cached = state.attractionEvents?.byId?.[eventId];

      // Category is required by TicketListing recommendations. Older cached
      // event payloads did not include it, so refresh incomplete cache entries.
      if (cached?.category) {
        return cached;
      }

      const event = await getEventById(eventId);
      return event;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Slice
const attractionEventsSlice = createSlice({
  name: 'attractionEvents',
  initialState: {
    byId: {}, // Normalized: { [eventId]: event }
    byAttraction: {}, // { [attractionId]: { eventIds: [], total: 0, currentPage: 0, lastFetched: number, hasMore: boolean } }
    selectedEvent: null,
    loading: false,
    error: null,
    // Legacy support
    eventsByAttraction: {},
  },
  reducers: {
    clearEvents: state => {
      state.eventsByAttraction = {};
      state.byAttraction = {};
      state.byId = {};
    },
    clearSelectedEvent: state => {
      state.selectedEvent = null;
    },
    // Clean expired cache entries
    cleanExpiredCache: state => {
      const now = Date.now();
      Object.keys(state.byAttraction).forEach(attractionId => {
        const cached = state.byAttraction[attractionId];
        if (cached.lastFetched && now - cached.lastFetched > EVENTS_CACHE_TTL) {
          // Keep the data but mark as stale (or remove if you prefer)
          delete cached.lastFetched;
        }
      });
    },
  },
  extraReducers: builder => {
    builder
      // Fetch events by attraction
      .addCase(fetchEventsByAttraction.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEventsByAttraction.fulfilled, (state, action) => {
        state.loading = false;
        const { attractionId, events, total, hasMore, currentPage } = action.payload;

        // Ensure attractionId is a number for consistent key usage
        const key = typeof attractionId === 'string' ? parseInt(attractionId) : attractionId;

        // Normalize: store events by ID
        if (events && events.length > 0) {
          events.forEach(event => {
            state.byId[event.id] = event;
          });
        }

        // Initialize or update byAttraction structure
        if (!state.byAttraction[key]) {
          state.byAttraction[key] = {
            eventIds: [],
            total: 0,
            currentPage: 0,
            hasMore: false,
            lastFetched: null,
          };
        }

        // If it's page 0, replace eventIds; otherwise append
        if (currentPage === 0) {
          state.byAttraction[key].eventIds = events?.map(e => e.id) || [];
        } else {
          const newIds = events?.map(e => e.id) || [];
          state.byAttraction[key].eventIds.push(...newIds);
        }

        state.byAttraction[key].total = total;
        state.byAttraction[key].currentPage = currentPage;
        state.byAttraction[key].hasMore = hasMore;
        state.byAttraction[key].lastFetched = Date.now();

        // Legacy support
        if (!state.eventsByAttraction[key]) {
          state.eventsByAttraction[key] = {
            events: [],
            total: 0,
            currentPage: 0,
            hasMore: false,
          };
        }
        if (currentPage === 0) {
          state.eventsByAttraction[key].events = events || [];
        } else {
          state.eventsByAttraction[key].events.push(...(events || []));
        }
        state.eventsByAttraction[key].total = total;
        state.eventsByAttraction[key].currentPage = currentPage;
        state.eventsByAttraction[key].hasMore = hasMore;
      })
      .addCase(fetchEventsByAttraction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch event details
      .addCase(fetchEventDetails.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEventDetails.fulfilled, (state, action) => {
        state.loading = false;
        const event = action.payload;

        // Normalize: store by ID
        if (event) {
          state.byId[event.id] = event;
          state.selectedEvent = event;
        }
      })
      .addCase(fetchEventDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearEvents, clearSelectedEvent, cleanExpiredCache } = attractionEventsSlice.actions;
export default attractionEventsSlice.reducer;
